"use strict";
/**
 * Генерация схем валидации свойств ProjectItem из единого реестра типов.
 *
 * Ни backend, ни frontend не описывают поля типов вручную: схема для каждого
 * из 15 типов собирается здесь по данным ITEM_TYPE_DEFINITIONS. Добавление
 * поля в реестр автоматически добавляет его валидацию.
 *
 * Клиентская валидация повышает удобство, но не заменяет серверную:
 * сервер валидирует теми же схемами перед записью в базу.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPropertiesSchema = getPropertiesSchema;
exports.getDetailSchema = getDetailSchema;
exports.toFieldErrors = toFieldErrors;
exports.validateProperties = validateProperties;
exports.validateDetail = validateDetail;
exports.assertRegistryIsConsistent = assertRegistryIsConsistent;
const contracts_1 = require("@project-os/contracts");
const zod_1 = require("zod");
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Строка допустимой длины, пустая строка приводится к отсутствию значения. */
function textSchema(property, defaultMax) {
    return zod_1.z
        .string()
        .trim()
        .max(property.maxLength ?? defaultMax, `Не длиннее ${property.maxLength ?? defaultMax} символов`);
}
/** Схема одного свойства без учёта обязательности. */
function baseSchemaFor(property) {
    switch (property.kind) {
        case 'string':
            return textSchema(property, 500);
        case 'text':
            return textSchema(property, 10_000);
        case 'url':
            return textSchema(property, 2000).refine((value) => /^https?:\/\/\S+$/i.test(value), 'Ссылка должна начинаться с http:// или https://');
        case 'email':
            return textSchema(property, 320)
                .toLowerCase()
                .refine((value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), 'Некорректный адрес');
        case 'integer':
            return zod_1.z.number().int('Только целое число');
        case 'decimal':
            return zod_1.z.number().finite('Некорректное число');
        case 'money':
            return zod_1.z
                .number()
                .int('Сумма хранится в минорных единицах и должна быть целым числом')
                .min(property.min ?? 0, 'Сумма не может быть отрицательной')
                .max(Number.MAX_SAFE_INTEGER);
        case 'currency':
            return zod_1.z.enum(contracts_1.CURRENCIES);
        case 'date':
            return zod_1.z
                .string()
                .regex(ISO_DATE, 'Дата в формате ГГГГ-ММ-ДД')
                .refine((value) => !Number.isNaN(Date.parse(value)), 'Несуществующая дата');
        case 'datetime':
            return zod_1.z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'Некорректная дата');
        case 'boolean':
            return zod_1.z.boolean();
        case 'enum': {
            const values = (property.options ?? []).map((option) => option.value);
            if (values.length === 0) {
                throw new Error(`Свойство ${property.key}: тип enum без списка значений`);
            }
            return zod_1.z.enum(values);
        }
        case 'userRefList':
            return zod_1.z.array(zod_1.z.uuid('Некорректный идентификатор пользователя')).max(50);
    }
}
/**
 * Схема свойства с учётом обязательности.
 * Необязательное свойство принимает null и отсутствие значения.
 */
function schemaFor(property, mode) {
    const base = baseSchemaFor(property);
    if (mode === 'partial' || !property.required) {
        return base.nullish();
    }
    return base;
}
const jsonSchemaCache = new Map();
const detailSchemaCache = new Map();
/**
 * Схема jsonb-свойств элемента.
 *
 * @param mode `full` — все обязательные поля требуются (полное сохранение);
 *             `partial` — все поля необязательны (быстрое добавление и PATCH).
 */
function getPropertiesSchema(type, mode = 'full') {
    const cacheKey = `${type}:${mode}`;
    const cached = jsonSchemaCache.get(cacheKey);
    if (cached)
        return cached;
    const shape = {};
    for (const property of (0, contracts_1.getJsonProperties)(type)) {
        shape[property.key] = schemaFor(property, mode);
    }
    // strict: неизвестный ключ в properties — ошибка, а не молчаливое сохранение.
    const schema = zod_1.z.strictObject(shape);
    jsonSchemaCache.set(cacheKey, schema);
    return schema;
}
/** Схема полей типизированной detail-таблицы (сейчас только EXPENSE). */
function getDetailSchema(type, mode = 'full') {
    const definition = (0, contracts_1.getItemTypeDefinition)(type);
    if (!definition.detailTable)
        return null;
    const cacheKey = `${type}:${mode}`;
    const cached = detailSchemaCache.get(cacheKey);
    if (cached)
        return cached;
    const shape = {};
    for (const property of (0, contracts_1.getDetailProperties)(type)) {
        shape[property.key] = schemaFor(property, mode);
    }
    const schema = zod_1.z.strictObject(shape);
    detailSchemaCache.set(cacheKey, schema);
    return schema;
}
/** Приводит ошибки zod к формату `fields` из контракта ошибок API. */
function toFieldErrors(error) {
    const fields = {};
    for (const issue of error.issues) {
        const path = issue.path.join('.') || '_';
        if (!(path in fields)) {
            fields[path] = issue.message;
        }
    }
    return fields;
}
function validateProperties(type, value, mode = 'full') {
    const result = getPropertiesSchema(type, mode).safeParse(value ?? {});
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, fieldErrors: toFieldErrors(result.error) };
}
function validateDetail(type, value, mode = 'full') {
    const schema = getDetailSchema(type, mode);
    if (!schema) {
        return { success: true, data: {} };
    }
    const result = schema.safeParse(value ?? {});
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, fieldErrors: toFieldErrors(result.error) };
}
/**
 * Проверка целостности реестра. Выполняется в тестах и при старте API,
 * чтобы опечатка в реестре не превратилась в ошибку времени выполнения.
 */
function assertRegistryIsConsistent() {
    for (const type of contracts_1.ITEM_TYPES) {
        const definition = (0, contracts_1.getItemTypeDefinition)(type);
        if (!definition.statuses.some((status) => status.value === definition.initialStatus)) {
            throw new Error(`Тип ${type}: начальный статус ${definition.initialStatus} отсутствует в списке статусов`);
        }
        const keys = new Set();
        for (const property of definition.properties) {
            if (keys.has(property.key)) {
                throw new Error(`Тип ${type}: свойство ${property.key} объявлено дважды`);
            }
            keys.add(property.key);
            if (property.kind === 'enum' && (property.options ?? []).length === 0) {
                throw new Error(`Тип ${type}: свойство ${property.key} типа enum без вариантов`);
            }
            if (property.storage === 'detail' && !definition.detailTable) {
                throw new Error(`Тип ${type}: свойство ${property.key} помечено как detail, но detailTable не задан`);
            }
        }
        for (const key of definition.quickAddProperties) {
            if (!keys.has(key)) {
                throw new Error(`Тип ${type}: quickAdd ссылается на несуществующее свойство ${key}`);
            }
        }
        // Схемы должны собираться без исключений.
        getPropertiesSchema(type, 'full');
        getPropertiesSchema(type, 'partial');
        getDetailSchema(type, 'full');
    }
}
//# sourceMappingURL=properties.js.map